import { Box, Flex, Text } from '@chakra-ui/react';
import {
  ButtonText,
  FileUpload,
  FormFeedback,
  OutlineCard
} from '@morressier/alembic';
import { FieldError, useFormContext } from 'react-hook-form';
import { DropzoneOptions, ErrorCode } from 'react-dropzone';
import { get } from 'lodash';
import { AxiosProgressEvent } from 'axios';
import { ISubmissionFile } from '@publishing-engine/domain/models';
import {
  FormFieldAnonymizedManuscript,
  FormFieldDoubleAnonymousPeerReview
} from '@publishing-engine/domain/types/article';

import { FieldLabel } from 'src/components/Field';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import { getFileExtension } from 'src/utils/file';
import {
  deleteUploadedFile,
  getSignedPrivateAccessUrl,
  getSignedPrivateUploadUrl
} from 'src/api/queries';
import axiosInstance from 'src/api/client';
import { RadioGroup } from 'src/components/RadioGroup';
import { getValidationTypedMessage } from 'src/components/FormField/common/errorMessages';

const DoubleAnonymousReview = ({
  submissionId,
  existsConfig,
  valueConfig
}: {
  submissionId: string;
  existsConfig: FormFieldDoubleAnonymousPeerReview;
  valueConfig: FormFieldAnonymizedManuscript;
}) => {
  const {
    formState: { errors },
    register,
    watch,
    setError,
    setValue,
    resetField,
    control
  } = useFormContext<ISubmissionFormData>();
  const {
    caseDataFields: { main: dependsOn },
    params: depends
  } = existsConfig;
  const {
    caseDataFields: { main: field },
    params: main
  } = valueConfig;

  const mainError = get(errors, field);
  const dependsOnError = get(errors, dependsOn);

  const doubleAnonymousPeerReview = watch(dependsOn);
  const anonymizedManuscript = watch(field);

  const handleOpenFile = async (url: string) => {
    if (!submissionId) {
      return;
    }
    const signedUrl = await getSignedPrivateAccessUrl(submissionId, url);
    window.open(signedUrl, '_blank');
  };

  const onDrop: DropzoneOptions['onDrop'] = async (acceptedFiles: File[]) => {
    const accepted = acceptedFiles[0];
    resetField(field);

    if (accepted) {
      anonymizedManuscript?.fileId &&
        deleteUploadedFile(anonymizedManuscript.fileId);

      const onUploadProgress = (progressEvent: AxiosProgressEvent) => {
        const { loaded, total = Number.POSITIVE_INFINITY } = progressEvent;
        const progress = Math.floor((loaded * 100) / total);

        fileToUpload = {
          ...fileToUpload,
          progress
        };

        setValue(field, fileToUpload);
      };

      const fileExtension = getFileExtension(accepted.name);
      let fileToUpload: ISubmissionFile = {
        name: accepted.name,
        referenceName: 'Anonymised Manuscript',
        progress: 0,
        isUploading: true,
        extension: fileExtension,
        url: ''
      };

      try {
        setValue(field, fileToUpload);

        const uploadParams = await getSignedPrivateUploadUrl(
          submissionId,
          fileExtension
        );
        if (!uploadParams) return;

        const formData = new FormData();
        formData.append('file', accepted, accepted.name);

        const {
          data: { id, file_url, original_filename }
        } = await axiosInstance.put<{
          id: string;
          file_url: string;
          original_filename: string;
        }>(uploadParams.signed_url, formData, {
          onUploadProgress
        });

        fileToUpload = {
          ...fileToUpload,
          fileId: id,
          url: file_url,
          name: original_filename,
          extension: fileExtension
        };

        setValue(field, fileToUpload);
      } catch (e) {
        const error = e as any;
        let errorMessage = 'File upload failed. Please try again.';

        // Handle specific error cases
        if (error?.response?.status === 413) {
          const fileSizeMB = (accepted.size / (1024 * 1024)).toFixed(1);
          errorMessage = `File size (${fileSizeMB}MB) exceeds the maximum allowed size of ${main.validations.maxSizeMB}MB. Please reduce the file size and try again.`;
        } else if (error?.message?.includes('413')) {
          const fileSizeMB = (accepted.size / (1024 * 1024)).toFixed(1);
          errorMessage = `File size (${fileSizeMB}MB) exceeds the maximum allowed size of ${main.validations.maxSizeMB}MB. Please reduce the file size and try again.`;
        } else if (error?.message) {
          errorMessage = `File upload failed: ${error.message}`;
        }

        setError(field, {
          type: 'upload-error',
          message: errorMessage
        });
      } finally {
        setValue(field, {
          ...fileToUpload,
          isUploading: false
        });
      }
    }
  };

  const onDropRejected: DropzoneOptions['onDropRejected'] = rejectedFiles => {
    if (rejectedFiles[0]) {
      removeFile(anonymizedManuscript?.fileId);
      const rejectedFileError = rejectedFiles[0].errors[0];
      if (rejectedFileError?.code === 'file-invalid-type') {
        setError(field, {
          type: rejectedFileError.code,
          message:
            'Selected file format is not supported. Please upload PDF, DOC, or DOCX only.'
        });
      }
      if (
        rejectedFileError?.code === ErrorCode.FileTooLarge ||
        rejectedFileError?.code === ErrorCode.TooManyFiles
      ) {
        setError(field, {
          type: rejectedFileError.code,
          message: rejectedFileError.message
        });
      }
    }
  };

  const removeFile = async (fileId?: string) => {
    setValue(field, undefined);
    fileId && deleteUploadedFile(fileId);
  };

  return (
    <Box>
      {/* ----- Double anonymous Review ----- */}
      <OutlineCard borderless width="100%" padding={8}>
        <Flex flexDirection="column" gap={4} width="100%">
          <FieldLabel
            label={depends.labels.main}
            subText={depends.labels.subtitle}
            isRequired={depends.validations.required}
          />

          <RadioGroup
            control={control}
            name={dependsOn}
            options={[
              {
                value: false,
                children: depends.labels.options['false']?.main
              },
              {
                value: true,
                children: depends.labels.options['true']?.main
              }
            ]}
          />
          {dependsOnError ? (
            <FormFeedback variant="error">
              Indicate your preference for double anonymous peer review before
              proceeding.
            </FormFeedback>
          ) : null}
        </Flex>
      </OutlineCard>

      {/* ----- Anonymised Manuscript Upload ----- */}
      {doubleAnonymousPeerReview ? (
        <OutlineCard borderless width="100%" padding={8}>
          <Flex flexDirection="column" gap={4} width="100%">
            <FieldLabel
              label={main.labels.main}
              subText={main.labels.subtitle}
              isRequired={main.validations.required}
            />

            <Box marginBottom={4}>
              <FileUpload
                {...register(field)}
                uploadButtonText="Select file"
                multiple={false}
                onDrop={onDrop}
                onDropRejected={onDropRejected}
                maxFiles={1}
                maxSize={main.validations.maxSizeMB * 1024 * 1024}
                isInvalid={Boolean(mainError)}
                accept={main.validations.allowedFileFormats}
                disabled={anonymizedManuscript?.isUploading}
              />
              <Text>
                Accepted file formats are "
                {Object.values(main.validations.allowedFileFormats)
                  .flat()
                  .join(', ')}
                ". The file size should not exceed {main.validations.maxSizeMB}
                MB.
              </Text>
              <Flex alignItems="center" gap={2}>
                {anonymizedManuscript?.isUploading ? (
                  <span>
                    {anonymizedManuscript.name}: Uploading{' '}
                    {anonymizedManuscript.progress}%
                  </span>
                ) : (
                  <>
                    {/* @Note: anonymizedManuscript field is depends on doubleAnonymousPeerReview field. 
                    anonymizedManuscript throws 'required' error but validation for anonymizedManuscript is set to false.
                    Because of that getValidationTypedMessage() can't generate correct message for that field. 
                    And that is why we have to handle this `mainError?.type === 'required'` separately here.*/}
                    {mainError?.type === 'required' ? (
                      <FormFeedback variant="error">
                        Please upload an {main.labels.main.toLowerCase()} before
                        continuing.
                      </FormFeedback>
                    ) : (
                      mainError && (
                        <FormFeedback variant="error">
                          {getValidationTypedMessage(
                            main.labels.main,
                            main.validations.kind,
                            main.validations,
                            mainError as FieldError
                          )}
                        </FormFeedback>
                      )
                    )}
                    {anonymizedManuscript?.fileId && (
                      <>
                        <a
                          onClick={() =>
                            handleOpenFile(anonymizedManuscript?.url)
                          }
                          style={{ cursor: 'pointer' }}
                        >
                          <FormFeedback variant="success">
                            {anonymizedManuscript?.name}
                          </FormFeedback>
                        </a>
                        <ButtonText
                          alignSelf={'flex-start'}
                          onClick={() =>
                            removeFile(anonymizedManuscript?.fileId)
                          }
                        >
                          Remove
                        </ButtonText>
                      </>
                    )}
                  </>
                )}
              </Flex>
            </Box>
          </Flex>
        </OutlineCard>
      ) : null}
    </Box>
  );
};

export default DoubleAnonymousReview;
